import { useState } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';

/**
 * パスワード2段入力 (新パスワード + 確認) + eye toggle + 一致インジケーター。
 * 設計: docs/design/v03-user-employee-refinement.md §5-4
 * ProfilePage (本人変更) / UsersPage (管理者リセット) 双方で共有する。
 */
export type PasswordSetInputProps = {
  value: string;
  confirmValue: string;
  onChange: (v: string) => void;
  onConfirmChange: (v: string) => void;
  minLength?: number;
  label?: string;
  confirmLabel?: string;
  autoComplete?: string;
  idPrefix: string;
};

export function PasswordSetInput({
  value, confirmValue, onChange, onConfirmChange,
  minLength = 8, label = '新しいパスワード', confirmLabel = '確認用', autoComplete = 'new-password', idPrefix,
}: PasswordSetInputProps) {
  const [showValue, setShowValue] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const bothFilled = value.length > 0 && confirmValue.length > 0;
  const matches = value === confirmValue;

  const field = (
    id: string, fieldLabel: string, v: string, setV: (s: string) => void,
    show: boolean, setShow: (b: boolean) => void, ac: string,
  ) => (
    <div>
      <label htmlFor={id} className="block text-sm text-neutral-sub mb-1">{fieldLabel}</label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={v}
          minLength={minLength}
          autoComplete={ac}
          onChange={(e) => setV(e.target.value)}
          className="w-full pl-3 pr-10 py-2 rounded border border-neutral-line focus:outline-none focus-ring font-mono"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          aria-label="パスワードを表示/非表示"
          aria-pressed={show}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-neutral-sub hover:text-text-main min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      {field(`${idPrefix}-password`, label, value, onChange, showValue, setShowValue, autoComplete)}
      {field(`${idPrefix}-password-confirm`, confirmLabel, confirmValue, onConfirmChange, showConfirm, setShowConfirm, autoComplete)}
      {bothFilled && (
        <div role="status" aria-live="polite" className={`flex items-center gap-1.5 text-xs ${matches ? 'text-success-text' : 'text-danger-text'}`}>
          {matches ? <Check size={14} /> : <X size={14} />}
          <span>{matches ? 'パスワードが一致しました' : 'パスワードが一致しません'}</span>
        </div>
      )}
    </div>
  );
}

/** 送信可否の共通判定 (両方入力済み・一致・最小長を満たす)。両ページの送信ボタンdisabled条件を統一する。 */
export const isPasswordSetValid = (value: string, confirmValue: string, minLength = 8): boolean =>
  value.length >= minLength && value === confirmValue;
